import { useCallback, useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../../../context/AuthContext';
import { trackConversion } from '../../../utils/gtmHelpers';

const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const SECTION_IDS = ['top', 'one-feed', 'score-breakdown', 'how-it-works', 'comparison', 'testimonials', 'faq'];

/**
 * Landing v2 analytics hook. Mirrors v1 event names so the funnel stays
 * comparable across versions, plus adds `landing_version: 'v2'` to every
 * payload. Fires:
 *   - landing_page_viewed (once on mount)
 *   - landing_scroll_depth (25/50/75/100, deduped)
 *   - landing_section_viewed (each section, deduped, via IntersectionObserver)
 *   - landing_cta_clicked (returned handler, fires PostHog + GTM)
 *   - landing_nav_link_clicked / landing_section_navigation (returned handler)
 */
export function useLandingAnalytics({ variant = 'default' } = {}) {
  const posthog = usePostHog();
  const { user } = useAuth() || {};
  const scrollDepthFiredRef = useRef(new Set());
  const sectionsSeenRef = useRef(new Set());

  useEffect(() => {
    if (!posthog?.capture) return;
    posthog.capture('landing_page_viewed', {
      timestamp: new Date().toISOString(),
      variant,
      landing_version: 'v2',
    });
  }, [posthog, variant]);

  useEffect(() => {
    if (typeof window === 'undefined' || !posthog?.capture) return undefined;
    const root = document.querySelector('.lp-v2-root');
    if (!root) return undefined;

    function onScroll() {
      const scrollTop = root.scrollTop;
      const scrollHeight = root.scrollHeight - root.clientHeight;
      if (scrollHeight <= 0) return;
      const pct = Math.round((scrollTop / scrollHeight) * 100);
      for (const t of SCROLL_THRESHOLDS) {
        if (pct >= t && !scrollDepthFiredRef.current.has(t)) {
          scrollDepthFiredRef.current.add(t);
          posthog.capture('landing_scroll_depth', {
            depth: `${t}%`,
            variant,
            landing_version: 'v2',
          });
        }
      }
    }

    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [posthog, variant]);

  useEffect(() => {
    if (typeof window === 'undefined' || !posthog?.capture) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const root = document.querySelector('.lp-v2-root');
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const sectionId = entry.target.id;
          if (sectionsSeenRef.current.has(sectionId)) continue;
          sectionsSeenRef.current.add(sectionId);
          posthog.capture('landing_section_viewed', {
            section: sectionId,
            variant,
            landing_version: 'v2',
          });
        }
      },
      { threshold: 0.4, root },
    );

    const els = SECTION_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [posthog, variant]);

  const onCtaPress = useCallback(
    (location, { ctaText } = {}) => {
      const userAuthenticated = Boolean(user);
      const props = {
        cta_location: location,
        variant,
        landing_version: 'v2',
        user_authenticated: userAuthenticated,
        ...(ctaText ? { cta_text: ctaText } : {}),
      };
      if (posthog?.capture) {
        posthog.capture('landing_cta_clicked', props);
      }
      try {
        trackConversion(
          'landing_cta_clicked',
          { user_id: user?.id, cta_location: location, user_authenticated: userAuthenticated },
          userAuthenticated ? user?.email : null,
        );
      } catch {
        /* GTM not loaded; safe to ignore */
      }
    },
    [posthog, user, variant],
  );

  const onNavPress = useCallback(
    (target) => {
      if (!posthog?.capture) return;
      if (typeof target !== 'string') return;
      if (target.startsWith('/')) {
        posthog.capture('landing_nav_link_clicked', { target_path: target, variant, landing_version: 'v2' });
      } else if (target.startsWith('#')) {
        posthog.capture('landing_section_navigation', { target_section: target, variant, landing_version: 'v2' });
      }
    },
    [posthog, variant],
  );

  return { onCtaPress, onNavPress };
}
